"use strict";
/**
 * ViewerTestBridge — dev-only test instrumentation.
 *
 * Exposes `window.__testAPI__` in development builds so Playwright tests can
 * query the Three.js scene graph directly without pixel-level assertions.
 *
 * Usage (called once in Viewer.tsx inside the NODE_ENV=development block):
 *   setupViewerTestBridge(this);
 *
 * Nothing in this file is imported in production — tree-shaking removes it
 * entirely, and the call-site is already inside `if (process.env.NODE_ENV === 'development')`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.setupViewerTestBridge = void 0;
const three_1 = require("three");
const consts_1 = require("./consts");
// World-space normals for each named face
// Face normals match the editor orientation cube:
//   top    = Z- = physically upward
//   bottom = Z+ = physically downward
//   front  = Y+ = toward viewer (camera-facing)
//   back   = Y- = away from viewer
//   right  = X+
//   left   = X-
const FACE_NORMALS = {
    top: new three_1.Vector3(0, 0, -1),
    bottom: new three_1.Vector3(0, 0, 1),
    front: new three_1.Vector3(0, 1, 0),
    back: new three_1.Vector3(0, -1, 0),
    left: new three_1.Vector3(-1, 0, 0),
    right: new three_1.Vector3(1, 0, 0),
};
function worldToScreen(viewer, pos) {
    const vec = new three_1.Vector3(pos.x, pos.y, pos.z);
    vec.project(viewer.camera);
    const rect = viewer.getRendererDOMElement().getBoundingClientRect();
    return {
        x: (vec.x * 0.5 + 0.5) * rect.width + rect.left,
        y: (-vec.y * 0.5 + 0.5) * rect.height + rect.top,
    };
}
/**
 * Walks from fraction=0.99 (right end) downward in 0.01 steps along the long axis
 * of the extrusion's +Y face, raycasting from the camera at each step.
 * Returns the screen position + worldX of the rightmost point that actually hits
 * this profile (not another overlapping object), so placeMinimalBar can reliably
 * target a +Y-face snap point instead of an end-cap X-snap.
 */
function getRightmostTopFaceScreen(viewer, profileIndex) {
    const profiles = viewer.getExtrusionProfiles();
    if (profileIndex >= profiles.length) {
        return null;
    }
    const profile = profiles[profileIndex];
    const bbox = new three_1.Box3().setFromObject(profile);
    const center = new three_1.Vector3();
    const size = new three_1.Vector3();
    bbox.getCenter(center);
    bbox.getSize(size);
    const dims = [
        { axis: 'x', len: size.x },
        { axis: 'y', len: size.y },
        { axis: 'z', len: size.z },
    ];
    dims.sort((a, b) => b.len - a.len);
    const longAxis = dims[0].axis;
    const normal = FACE_NORMALS['front']; // Y+ — viewer-facing face
    const rect = viewer.getRendererDOMElement().getBoundingClientRect();
    const raycaster = new three_1.Raycaster();
    for (let f = 0.99; f >= 0.70; f -= 0.01) {
        const facePt = center.clone().add(normal.clone().multiply(size.clone().multiplyScalar(0.5)));
        facePt[longAxis] = bbox.min[longAxis] + f * (bbox.max[longAxis] - bbox.min[longAxis]);
        // Skip points within 1" of the end cap — the app prefers the X-axis end-cap snap there
        if (facePt[longAxis] > bbox.max[longAxis] - 1.0) {
            continue;
        }
        const screenPos = worldToScreen(viewer, facePt);
        const ndcX = ((screenPos.x - rect.left) / rect.width) * 2 - 1;
        const ndcY = -((screenPos.y - rect.top) / rect.height) * 2 + 1;
        raycaster.setFromCamera(new three_1.Vector2(ndcX, ndcY), viewer.camera);
        const hits = raycaster.intersectObjects(profiles, true);
        if (hits.length === 0) {
            continue;
        }
        const hitObj = hits[0].object;
        const hitIndex = profiles.findIndex((p) => { var _a; return p === hitObj || ((_a = p.getObjectById) === null || _a === void 0 ? void 0 : _a.call(p, hitObj.id)) !== undefined; });
        if (hitIndex === profileIndex) {
            return {
                x: screenPos.x,
                y: screenPos.y,
                worldX: +facePt.x.toFixed(2),
                fraction: +f.toFixed(2),
            };
        }
    }
    return null;
}
function getSideFaceCenterScreen(viewer, profileIndex, face) {
    const profiles = viewer.getExtrusionProfiles();
    if (profileIndex >= profiles.length) {
        return null;
    }
    const bbox = new three_1.Box3().setFromObject(profiles[profileIndex]);
    const center = new three_1.Vector3();
    const size = new three_1.Vector3();
    bbox.getCenter(center);
    bbox.getSize(size);
    const normal = FACE_NORMALS[face];
    if (!normal) {
        return null;
    }
    const facePt = center.clone().add(normal.clone().multiply(size.clone().multiplyScalar(0.5)));
    return worldToScreen(viewer, facePt);
}
function getSideFaceAtFractionScreen(viewer, profileIndex, face, fraction) {
    const profiles = viewer.getExtrusionProfiles();
    if (profileIndex >= profiles.length) {
        return null;
    }
    const profile = profiles[profileIndex];
    const bbox = new three_1.Box3().setFromObject(profile);
    const center = new three_1.Vector3();
    const size = new three_1.Vector3();
    bbox.getCenter(center);
    bbox.getSize(size);
    // Find the long axis
    const dims = [
        { axis: 'x', len: size.x },
        { axis: 'y', len: size.y },
        { axis: 'z', len: size.z },
    ];
    dims.sort((a, b) => b.len - a.len);
    const longAxis = dims[0].axis;
    const normal = FACE_NORMALS[face];
    if (!normal) {
        return null;
    }
    const facePt = center.clone().add(normal.clone().multiply(size.clone().multiplyScalar(0.5)));
    facePt[longAxis] = bbox.min[longAxis] + fraction * (bbox.max[longAxis] - bbox.min[longAxis]);
    return worldToScreen(viewer, facePt);
}
function getDetailedSceneState(viewer) {
    return {
        extrusions: viewer.getExtrusionProfiles().map((p, i) => {
            var _a;
            const bbox = new three_1.Box3().setFromObject(p);
            const center = new three_1.Vector3();
            const size = new three_1.Vector3();
            bbox.getCenter(center);
            bbox.getSize(size);
            const dn = (_a = p.directionNormal) !== null && _a !== void 0 ? _a : p.direction_normal;
            return {
                idx: i,
                center: { x: +center.x.toFixed(2), y: +center.y.toFixed(2), z: +center.z.toFixed(2) },
                size: { x: +size.x.toFixed(2), y: +size.y.toFixed(2), z: +size.z.toFixed(2) },
                dn: dn ? { x: +dn.x.toFixed(3), y: +dn.y.toFixed(3), z: +dn.z.toFixed(3) } : null,
                screenCenter: worldToScreen(viewer, center),
            };
        }),
        connections: viewer.getAllConnections().map((c, i) => {
            var _a, _b, _c;
            const pos = new three_1.Vector3();
            const sphere = (_a = c.getSphere) === null || _a === void 0 ? void 0 : _a.call(c);
            if (sphere) {
                sphere.getWorldPosition(pos);
            }
            else {
                c.getWorldPosition(pos);
            }
            return {
                idx: i,
                uuid: c.uuid.substring(0, 8),
                worldPos: { x: +pos.x.toFixed(2), y: +pos.y.toFixed(2), z: +pos.z.toFixed(2) },
                screenPos: worldToScreen(viewer, pos),
                children: c.children.length,
                fastenerCount: (_c = (_b = c.getFastenerCount) === null || _b === void 0 ? void 0 : _b.call(c)) !== null && _c !== void 0 ? _c : -1,
            };
        }),
        components: viewer.getComponents().length,
        fasteners: viewer.getFasteners().length,
    };
}
function getConnectionScreenPos(viewer, index) {
    var _a;
    const connections = viewer.getAllConnections();
    if (index >= connections.length) {
        return null;
    }
    const conn = connections[index];
    const pos = new three_1.Vector3();
    const sphere = (_a = conn.getSphere) === null || _a === void 0 ? void 0 : _a.call(conn);
    if (sphere) {
        sphere.getWorldPosition(pos);
    }
    else {
        conn.getWorldPosition(pos);
    }
    return worldToScreen(viewer, pos);
}
/** Install `window.__testAPI__` — call once from Viewer.tsx inside the development guard. */
function setupViewerTestBridge(viewer) {
    window.__testAPI__ = {
        // === RAW SCENE ACCESS ===
        viewer,
        getScene: () => viewer.scene,
        getExtrusionProfiles: () => viewer.getExtrusionProfiles(),
        getComponents: () => viewer.getComponents(),
        getFasteners: () => viewer.getFasteners(),
        getAllConnections: () => viewer.getAllConnections(),
        // === PANEL QUERIES ===
        /** Returns the count of installed (non-inserting) panels in the scene. */
        getPanelCount: () => viewer.getPanels().length,
        /** Returns basic state for all installed panels (excludes the panel currently being inserted). */
        getPanels: () => viewer.getPanels().map((panel, idx) => {
            var _a;
            const bbox = new three_1.Box3().setFromObject(panel);
            const center = new three_1.Vector3();
            const size = new three_1.Vector3();
            bbox.getCenter(center);
            bbox.getSize(size);
            return {
                idx,
                uuid: panel.uuid.substring(0, 8),
                itemNumber: (_a = panel.itemNumber) !== null && _a !== void 0 ? _a : null,
                center: { x: +center.x.toFixed(2), y: +center.y.toFixed(2), z: +center.z.toFixed(2) },
                size: { x: +size.x.toFixed(2), y: +size.y.toFixed(2), z: +size.z.toFixed(2) },
                screenCenter: worldToScreen(viewer, center),
                isCollided: !!panel.isCollided,
            };
        }),
        /** Returns true if any installed panel is currently in collided/error state. */
        hasPanelCollision: () => viewer.getPanels().some((p) => !!p.isCollided),
        /** Returns the panel that is currently being inserted (attached to cursor), or null. */
        getInsertingPanel: () => {
            var _a;
            const scene = viewer.scene;
            const inserting = scene.children.find((c) => {
                var _a;
                return c &&
                    (c.type === 'Panel' || ((_a = c.constructor) === null || _a === void 0 ? void 0 : _a.name) === 'Panel') &&
                    viewer.isObjectUnderInsertion(c);
            });
            if (!inserting) {
                return null;
            }
            const bbox = new three_1.Box3().setFromObject(inserting);
            const center = new three_1.Vector3();
            const size = new three_1.Vector3();
            bbox.getCenter(center);
            bbox.getSize(size);
            return {
                uuid: inserting.uuid.substring(0, 8),
                itemNumber: (_a = inserting.itemNumber) !== null && _a !== void 0 ? _a : null,
                center: { x: +center.x.toFixed(2), y: +center.y.toFixed(2), z: +center.z.toFixed(2) },
                size: { x: +size.x.toFixed(2), y: +size.y.toFixed(2), z: +size.z.toFixed(2) },
                screenCenter: worldToScreen(viewer, center),
                isCollided: !!inserting.isCollided,
                visible: !!inserting.visible,
            };
        },
        /** Returns the current panel-insertion error message, or null. */
        getPanelInsertionError: () => { var _a, _b, _c; return (_c = (_b = (_a = viewer.designProMain) === null || _a === void 0 ? void 0 : _a.panelInsertHelper) === null || _b === void 0 ? void 0 : _b.insertionErrorMessage) !== null && _c !== void 0 ? _c : null; },
        // === SNAP / HIGHLIGHT ===
        getSnappingHighlightMeshes: () => { var _a; return (_a = viewer.designProMain.highlightingTool.snappingHighlightMeshes) !== null && _a !== void 0 ? _a : []; },
        // Returns current snap highlight info: whether it's a center snap, and the
        // world-space position of the highlighted snap target.  Returns null if no
        // snap highlight is active.
        getSnapInfo: () => {
            var _a, _b, _c, _d, _e, _f;
            const meshes = (_a = viewer.designProMain.highlightingTool.snappingHighlightMeshes) !== null && _a !== void 0 ? _a : [];
            if (!meshes.length) {
                return null;
            }
            const mesh = meshes[0];
            const isCenter = ((_e = (_d = (_c = (_b = mesh.material) === null || _b === void 0 ? void 0 : _b.color) === null || _c === void 0 ? void 0 : _c.getHex) === null || _d === void 0 ? void 0 : _d.call(_c)) !== null && _e !== void 0 ? _e : 0) === 0x50fffd;
            // For slot-type highlights the geometry vertices are in world space and
            // mesh.position is (0,0,0).  Compute the centroid from the bounding box.
            let worldPos;
            if (((_f = mesh.geometry) === null || _f === void 0 ? void 0 : _f.type) === 'BufferGeometry') {
                if (!mesh.geometry.boundingBox) {
                    mesh.geometry.computeBoundingBox();
                }
                const center = new three_1.Vector3();
                mesh.geometry.boundingBox.getCenter(center);
                worldPos = { x: center.x, y: center.y, z: center.z };
            }
            else {
                worldPos = { x: mesh.position.x, y: mesh.position.y, z: mesh.position.z };
            }
            return { isCenter, worldPos, meshCount: meshes.length };
        },
        worldToScreen: (pos) => worldToScreen(viewer, pos),
        // === SCENE QUERIES ===
        getAABBoxCenterScreen: (profileIndex) => {
            const profiles = viewer.getExtrusionProfiles();
            if (profileIndex >= profiles.length) {
                return null;
            }
            const center = new three_1.Vector3();
            new three_1.Box3().setFromObject(profiles[profileIndex]).getCenter(center);
            return worldToScreen(viewer, center);
        },
        getSideFaceCenterScreen: (profileIndex, face) => getSideFaceCenterScreen(viewer, profileIndex, face),
        getSideFaceAtFractionScreen: (profileIndex, face, fraction) => getSideFaceAtFractionScreen(viewer, profileIndex, face, fraction),
        getRightmostTopFaceScreen: (profileIndex) => getRightmostTopFaceScreen(viewer, profileIndex),
        getDetailedSceneState: () => getDetailedSceneState(viewer),
        /** Returns state for a single extrusion without serializing the entire scene. */
        getExtrusion: (profileIndex) => {
            var _a;
            const profiles = viewer.getExtrusionProfiles();
            if (profileIndex >= profiles.length) {
                return null;
            }
            const p = profiles[profileIndex];
            const bbox = new three_1.Box3().setFromObject(p);
            const center = new three_1.Vector3();
            const size = new three_1.Vector3();
            bbox.getCenter(center);
            bbox.getSize(size);
            const dn = (_a = p.directionNormal) !== null && _a !== void 0 ? _a : p.direction_normal;
            return {
                idx: profileIndex,
                center: { x: +center.x.toFixed(2), y: +center.y.toFixed(2), z: +center.z.toFixed(2) },
                size: { x: +size.x.toFixed(2), y: +size.y.toFixed(2), z: +size.z.toFixed(2) },
                dn: dn ? { x: +dn.x.toFixed(3), y: +dn.y.toFixed(3), z: +dn.z.toFixed(3) } : null,
                screenCenter: worldToScreen(viewer, center),
            };
        },
        getConnectionScreenPos: (index) => getConnectionScreenPos(viewer, index),
        // Returns all slot surface-points (world-space) for a given extrusion.
        getExtrusionSlots: (profileIndex) => {
            var _a, _b;
            const profiles = viewer.getExtrusionProfiles();
            if (profileIndex >= profiles.length) {
                return [];
            }
            const ext = profiles[profileIndex];
            const slots = (_b = (_a = ext.getSlots) === null || _a === void 0 ? void 0 : _a.call(ext)) !== null && _b !== void 0 ? _b : [];
            return slots.map((s) => {
                var _a;
                return ({
                    surfacePoint: { x: s.surfacePoint.x, y: s.surfacePoint.y, z: s.surfacePoint.z },
                    normal: { x: s.normal.x, y: s.normal.y, z: s.normal.z },
                    slotNumber: (_a = s.slotNumber) !== null && _a !== void 0 ? _a : -1,
                });
            });
        },
        // Returns slot surface-points for the in-progress inserted object, if any.
        getInsertedObjectSlots: () => {
            var _a, _b, _c, _d;
            const main = viewer.designProMain;
            const obj = (_c = (_a = main === null || main === void 0 ? void 0 : main.insertedObject) !== null && _a !== void 0 ? _a : (_b = main === null || main === void 0 ? void 0 : main.scalingTool) === null || _b === void 0 ? void 0 : _b.object) !== null && _c !== void 0 ? _c : null;
            if (!(obj === null || obj === void 0 ? void 0 : obj.getSlots)) {
                return [];
            }
            const slots = (_d = obj.getSlots()) !== null && _d !== void 0 ? _d : [];
            return slots.map((s) => {
                var _a;
                return ({
                    surfacePoint: { x: s.surfacePoint.x, y: s.surfacePoint.y, z: s.surfacePoint.z },
                    normal: { x: s.normal.x, y: s.normal.y, z: s.normal.z },
                    slotNumber: (_a = s.slotNumber) !== null && _a !== void 0 ? _a : -1,
                });
            });
        },
        // Returns all snapping points (inbound + outbound) for a given extrusion.
        getExtrusionSnappingPoints: (profileIndex) => {
            var _a, _b, _c, _d;
            const profiles = viewer.getExtrusionProfiles();
            if (profileIndex >= profiles.length) {
                return [];
            }
            const ext = profiles[profileIndex];
            const inbound = (_b = (_a = ext.getInboundSnappingPoints) === null || _a === void 0 ? void 0 : _a.call(ext)) !== null && _b !== void 0 ? _b : [];
            const outbound = (_d = (_c = ext.getOutboundSnappingPoints) === null || _c === void 0 ? void 0 : _c.call(ext)) !== null && _d !== void 0 ? _d : [];
            const result = [];
            for (const sp of inbound) {
                result.push({
                    origin: { x: sp.origin.x, y: sp.origin.y, z: sp.origin.z },
                    normal: { x: sp.normal.x, y: sp.normal.y, z: sp.normal.z },
                    type: 'inbound',
                });
            }
            for (const sp of outbound) {
                result.push({
                    origin: { x: sp.origin.x, y: sp.origin.y, z: sp.origin.z },
                    normal: { x: sp.normal.x, y: sp.normal.y, z: sp.normal.z },
                    type: 'outbound',
                });
            }
            return result;
        },
        // === PROGRAMMATIC CONTROL ===
        // Opens the RecommendedFastenersWidget for the given connection via sendSphereData.
        openRecommendedFastenersWidget: (connIndex) => {
            const conns = viewer.getAllConnections();
            const conn = connIndex === -1 ? conns.at(-1) : conns[connIndex];
            if (conn) {
                viewer.sendSphereData(conn.uuid, 640, 360);
            }
        },
        // Returns true if the viewer is currently in Show Fasteners mode (extrusions are at reduced opacity).
        isFastenerModeActive: () => {
            var _a, _b, _c;
            const profiles = viewer.getExtrusionProfiles();
            if (!profiles.length) {
                return false;
            }
            return ((_c = (_b = (_a = profiles[0].mesh) === null || _a === void 0 ? void 0 : _a.material) === null || _b === void 0 ? void 0 : _b.opacity) !== null && _c !== void 0 ? _c : 1) < 0.9;
        },
        // === PLACEMENT STATE ===
        // True when a catalog item model has finished loading and the app is in placement drag mode
        // (DragControls active, ready for first placement click).
        // Use this instead of waitForTimeout(200) before the first placement click.
        isDraggingInProgress: () => viewer.designProMain.draggingInProgress,
        // True when the first placement click has been committed (insertObjectPreviewToTheScene succeeded)
        // and the app is in extend/scale mode, waiting for the second placement click.
        // Use this instead of waitForTimeout(500) after the first placement click.
        isScalingInProgress: () => viewer.designProMain.scalingInProgress,
        // The axis along which the ScalingTool is currently extending ('x', 'y', or 'z').
        // Read after the axis-hint mouse move in DrawCrossbar to verify the correct axis was detected
        // before starting the creep loop.
        getScalingDirection: () => viewer.designProMain.scalingDirection,
        // Returns the scene index of the most recently placed extrusion (profiles.length - 1). Safe to call after waitForExtrusionCount confirms +1.
        getLastPlacedExtrusionIdx: () => viewer.getExtrusionProfiles().length - 1,
        // Polls until at least one snap-highlight mesh is visible (approach settled on a snap target).
        waitForSnapHighlight: (timeout = 3000) => new Promise((resolve) => {
            const check = () => {
                var _a;
                const meshes = (_a = viewer.designProMain.highlightingTool.snappingHighlightMeshes) !== null && _a !== void 0 ? _a : [];
                if (meshes.length > 0) {
                    resolve(true);
                    return;
                }
                if (Date.now() - start > timeout) {
                    resolve(false);
                    return;
                }
                requestAnimationFrame(check);
            };
            const start = Date.now();
            requestAnimationFrame(check);
        }),
        // Resizes an extrusion by scene index — same code path as typing a value in the resize label input.
        resizeExtrusionByIndex: (index, lengthInches) => {
            const profiles = viewer.getExtrusionProfiles();
            if (index >= profiles.length) {
                return;
            }
            const uuid = profiles[index].uuid;
            viewer.changeItemSize(uuid, { z: lengthInches }, true);
        },
        // Resizes a Y-axis bar (at scene index `idx`) so its free end reaches world-Y `targetY`.
        //
        // Mirrors the UI PZPicker drag path exactly:
        //   setExtrusionReverseScale → changeItemSize → setExtrusionForwardScale
        // The south end (bbox.min.y) stays fixed; the north free end moves to targetY.
        //
        // NOTE: this is coupled to TransformControlsInitial.setExtrusionReverseScale/ForwardScale.
        // If those methods change, update this bridge accordingly.
        resizeExtrusionToY: (idx, targetY) => {
            const profiles = viewer.getExtrusionProfiles();
            if (idx >= profiles.length) {
                return;
            }
            const profile = profiles[idx];
            // newLength is the distance from the fixed south end to targetY.
            // Must be computed BEFORE setExtrusionReverseScale moves the origin.
            const bbox = new three_1.Box3().setFromObject(profile);
            const newLength = targetY - bbox.min.y;
            if (newLength <= 0) {
                throw new Error(`resizeExtrusionToY: computed length ${newLength.toFixed(3)} <= 0 for idx=${idx}, targetY=${targetY}. ` +
                    `bbox=[${bbox.min.y.toFixed(3)}, ${bbox.max.y.toFixed(3)}]`);
            }
            // Flip to south anchor (mirrors PZPicker mouseDown), scale, then restore orientation.
            const tc = viewer.getDesignProMain().transformControlsInitial;
            const savedTcObject = tc.object;
            tc.object = profile;
            tc.isReverseScale = false;
            tc.setExtrusionReverseScale();
            viewer.changeItemSize(profile.uuid, { z: newLength }, true);
            tc.setExtrusionForwardScale();
            tc.object = savedTcObject;
        },
        // Moves an extrusion to the given AABB-center coords — converts to object-origin to handle per-axis geometry offsets.
        moveExtrusionTo: (index, x, y, z) => {
            const profiles = viewer.getExtrusionProfiles();
            if (index >= profiles.length) {
                return;
            }
            const profile = profiles[index];
            const bbox = new three_1.Box3().setFromObject(profile);
            const aabbCenter = new three_1.Vector3();
            bbox.getCenter(aabbCenter);
            // Offset from AABB center to object origin — preserved across the move.
            const ox = profile.position.x - aabbCenter.x;
            const oy = profile.position.y - aabbCenter.y;
            const oz = profile.position.z - aabbCenter.z;
            viewer.changeItemPosition(profile.uuid, { x: x + ox, y: y + oy, z: z + oz });
        },
        // Moves via TC mouseUp path (getTransformObjects + SetPositionCommand). Use moveExtrusionTo for connection-forming moves.
        moveExtrusionViaTC: (index, x, y, z) => {
            const profiles = viewer.getExtrusionProfiles();
            if (index >= profiles.length) {
                return;
            }
            const profile = profiles[index];
            const bbox = new three_1.Box3().setFromObject(profile);
            const aabbCenter = new three_1.Vector3();
            bbox.getCenter(aabbCenter);
            const ox = profile.position.x - aabbCenter.x;
            const oy = profile.position.y - aabbCenter.y;
            const oz = profile.position.z - aabbCenter.z;
            const dm = viewer.getDesignProMain();
            const tc = dm.transformControlsInitial;
            tc.setMode('translate');
            tc.attach(profile);
            profile.positionOnDown = profile.position.clone();
            profile.quaternionOnDown = profile.quaternion.clone();
            profile.scaleOnDown = profile.getScale ? profile.getScale().clone() : profile.scale.clone();
            profile.setPosition(new three_1.Vector3(x + ox, y + oy, z + oz));
            profile.updateMatrixWorld(true);
            tc.dispatchEvent({ type: 'mouseUp' });
            tc.detach();
        },
        // === CAMERA ===
        // Zooms the camera out by repositioning it farther from the orbit target.
        // factor=2 doubles the view size; useful before placing items in large world coordinates.
        zoomOutCamera: (factor) => {
            const cam = viewer.camera;
            const controls = viewer.orbitControls;
            if (!controls) {
                return;
            }
            const offset = cam.position.clone().sub(controls.target);
            offset.multiplyScalar(factor);
            cam.position.copy(controls.target.clone().add(offset));
            controls.update();
        },
        // Zooms the camera to fit all extrusion profiles — useful after programmatic placement.
        fitCameraToScene: () => {
            const profiles = viewer.getExtrusionProfiles();
            if (!profiles.length) {
                return;
            }
            const box = new three_1.Box3();
            profiles.forEach((p) => box.union(new three_1.Box3().setFromObject(p)));
            if (!box.isEmpty()) {
                viewer.zoomToFit(box);
            }
        },
        // === UI HANDLE POSITIONS ===
        // Returns the screen position where the mouse should be placed during bar scaling
        // so that the bar extends to `targetCoord` along `barAxis`. Uses the same
        // reference frame as ScalingTool (slide plane at the inserted object's Z).
        // Must be called AFTER the first click (when scalingInProgress is true).
        getScreenForBarTarget: (targetCoord, barAxis) => {
            var _a, _b, _c;
            const main = viewer.designProMain;
            const initPos = main.insertedObjectInitialPosition;
            const slidePlaneZ = (_c = (_b = (_a = viewer.slidePlane) === null || _a === void 0 ? void 0 : _a.position) === null || _b === void 0 ? void 0 : _b.z) !== null && _c !== void 0 ? _c : initPos.z;
            const worldPt = barAxis === 'x'
                ? { x: targetCoord, y: initPos.y, z: slidePlaneZ }
                : { x: initPos.x, y: targetCoord, z: slidePlaneZ };
            const screen = worldToScreen(viewer, worldPt);
            return {
                x: screen.x,
                y: screen.y,
                _initPos: { x: initPos.x, y: initPos.y, z: initPos.z },
                _slidePlaneZ: slidePlaneZ,
                _worldPt: worldPt,
            };
        },
        /** Returns the in-progress bar's AABB during scaling mode. Reads from scalingTool, not profiles list. */
        getScalingBarExtent: () => {
            var _a;
            const main = viewer.designProMain;
            const obj = (_a = main === null || main === void 0 ? void 0 : main.scalingTool) === null || _a === void 0 ? void 0 : _a.object;
            if (!obj) {
                return null;
            }
            const bbox = new three_1.Box3().setFromObject(obj);
            const center = new three_1.Vector3();
            const size = new three_1.Vector3();
            bbox.getCenter(center);
            bbox.getSize(size);
            return {
                center: { x: center.x, y: center.y, z: center.z },
                size: { x: size.x, y: size.y, z: size.z },
                // Maximum scale before collision — used by DrawCrossbar to detect an obstacle
                maxCollisionScale: main.scalingTool.maxScaleBeforeCollision,
            };
        },
        // Returns current camera state: world direction, up vector, position, and orbit target.
        getCameraState: () => {
            const cam = viewer.camera;
            const dir = new three_1.Vector3();
            cam.getWorldDirection(dir);
            const controls = viewer.orbitControls;
            const target = controls ? controls.target.clone() : new three_1.Vector3();
            return {
                direction: { x: dir.x, y: dir.y, z: dir.z },
                up: { x: cam.up.x, y: cam.up.y, z: cam.up.z },
                position: { x: cam.position.x, y: cam.position.y, z: cam.position.z },
                target: { x: target.x, y: target.y, z: target.z },
            };
        },
        // Returns the default camera position configured in consts.
        getDefaultCameraPosition: () => ({
            x: consts_1.consts.DEFAULT_CAMERA_POSITION.x,
            y: consts_1.consts.DEFAULT_CAMERA_POSITION.y,
            z: consts_1.consts.DEFAULT_CAMERA_POSITION.z,
        }),
        // Returns all connections that involve the given extrusion (by profile index).
        // For each connection, reports its world position and the indices of all OTHER
        // extrusions involved in that connection.
        // Use this to assert "bar X is connected to column Y" without guessing coordinates.
        getConnectionsForExtrusion: (profileIndex) => {
            const profiles = viewer.getExtrusionProfiles();
            if (profileIndex >= profiles.length) {
                return [];
            }
            const targetUUID = profiles[profileIndex].uuid;
            const connections = viewer.getAllConnections();
            const result = [];
            connections.forEach((conn, connIdx) => {
                var _a, _b, _c, _d, _e;
                const mainUUID = (_b = (_a = conn.getMainObject) === null || _a === void 0 ? void 0 : _a.call(conn)) === null || _b === void 0 ? void 0 : _b.uuid;
                const connectedUUID = (_d = (_c = conn.getConnectedObject) === null || _c === void 0 ? void 0 : _c.call(conn)) === null || _d === void 0 ? void 0 : _d.uuid;
                const isInvolved = mainUUID === targetUUID || connectedUUID === targetUUID;
                if (!isInvolved) {
                    return;
                }
                // Find partner extrusion indices (all extrusions in this connection that are NOT the target)
                const partnerIndices = [];
                [mainUUID, connectedUUID].forEach((uuid) => {
                    if (!uuid || uuid === targetUUID) {
                        return;
                    }
                    const idx = profiles.findIndex((p) => p.uuid === uuid);
                    if (idx !== -1) {
                        partnerIndices.push(idx);
                    }
                });
                // World position of the connection sphere
                const pos = new three_1.Vector3();
                const sphere = (_e = conn.getSphere) === null || _e === void 0 ? void 0 : _e.call(conn);
                if (sphere) {
                    sphere.getWorldPosition(pos);
                }
                else {
                    conn.getWorldPosition(pos);
                }
                result.push({
                    connectionIdx: connIdx,
                    worldPos: { x: +pos.x.toFixed(2), y: +pos.y.toFixed(2), z: +pos.z.toFixed(2) },
                    partnerExtrusionIndices: partnerIndices,
                });
            });
            return result;
        },
        // Returns the extrusion indices that a component (corner bracket, end cap, etc.) is connected to.
        // Uses the component's connectedItems list — separate from extrusion-extrusion ConnectionObjects.
        getComponentConnectedExtrusionIndices: (componentIndex) => {
            var _a;
            const components = viewer.getComponents();
            if (componentIndex >= components.length) {
                return [];
            }
            const comp = components[componentIndex];
            const profiles = viewer.getExtrusionProfiles();
            return ((_a = comp.connectedItems) !== null && _a !== void 0 ? _a : [])
                .filter((item) => item.itemType === 'Extrusion')
                .map((item) => profiles.findIndex((p) => p.uuid === item.uuid))
                .filter((idx) => idx !== -1);
        },
        // of an extrusion by profile index.
        // physicalTop = center.z - size.z/2  (remember: -Z is UP in this scene)
        // physicalBottom = center.z + size.z/2
        getExtrusionMinMaxZ: (profileIndex) => {
            const profiles = viewer.getExtrusionProfiles();
            if (profileIndex >= profiles.length) {
                return null;
            }
            const bbox = new three_1.Box3().setFromObject(profiles[profileIndex]);
            return {
                physicalTop: +bbox.min.z.toFixed(2),
                physicalBottom: +bbox.max.z.toFixed(2),
                minZ: +bbox.min.z.toFixed(2),
                maxZ: +bbox.max.z.toFixed(2),
            };
        },
        /**
         * Returns screen positions of the two resize handles for the currently active
         * SCALE mode (Resize tool).
         *
         * For PANELS: uses panelCubes[0/1].
         * For EXTRUSIONS: computes handle positions from the bar's bounding box.
         *   - fixedEnd = the end that is CLOSER to the origin (col-BR side for a Y-bar).
         *     = center - directionNormal * size/2
         *   - freeEnd  = the end that should be DRAGGED (toward col-FR).
         *     = center + directionNormal * size/2
         *
         * Note: for isReverseScale bars the direction is flipped, but since we derive
         * positions purely from the AABB the result is always correct.
         *
         * Returns null if resize mode is not active.
         */
        getResizeHandlesScreenPos: () => {
            var _a;
            const { scalingTool, transformControlsInitial: tc } = viewer.designProMain;
            // For panels: use panelCubes (existing behavior)
            if (scalingTool && scalingTool.panelCubes.length >= 2) {
                const fixed = scalingTool.panelCubes[0].position;
                const free = scalingTool.panelCubes[1].position;
                return {
                    fixedEnd: worldToScreen(viewer, fixed),
                    freeEnd: worldToScreen(viewer, free),
                };
            }
            // For extrusions in SCALE mode: derive handle positions from the AABB.
            if (!(tc === null || tc === void 0 ? void 0 : tc.object) || tc.mode !== 'scale') {
                return null;
            }
            const obj = tc.object;
            const bbox = new three_1.Box3().setFromObject(obj);
            const center = new three_1.Vector3();
            const size = new three_1.Vector3();
            bbox.getCenter(center);
            bbox.getSize(size);
            // Determine the primary (drawn) axis from directionNormal — use dn, not size,
            // because a minimal bar's cross-section can exceed its drawn length.
            const dn = ((_a = obj.directionNormal) !== null && _a !== void 0 ? _a : obj.direction_normal);
            let longAxis = 'z'; // extrusion default: local Z = drawn length
            if (dn) {
                // directionNormal points along the drawn axis in world space.
                // Pick the world axis with the largest absolute dn component.
                if (Math.abs(dn.x) > Math.abs(dn.y) && Math.abs(dn.x) > Math.abs(dn.z)) {
                    longAxis = 'x';
                }
                else if (Math.abs(dn.y) > Math.abs(dn.z)) {
                    longAxis = 'y';
                }
                else {
                    longAxis = 'z';
                }
            }
            // fixedEnd = the end at min along long axis
            const fixedPt = center.clone();
            fixedPt[longAxis] = bbox.min[longAxis];
            // freeEnd = the end at max along long axis
            const freePt = center.clone();
            freePt[longAxis] = bbox.max[longAxis];
            return {
                fixedEnd: worldToScreen(viewer, { x: fixedPt.x, y: fixedPt.y, z: fixedPt.z }),
                freeEnd: worldToScreen(viewer, { x: freePt.x, y: freePt.y, z: freePt.z }),
            };
        },
        /**
         * Returns screen positions for the X, Y, Z translate arrow tips.
         * The TC gizmo is positioned at the attached object's world origin. Arrow tips are
         * at (origin + axis * scale * 0.5) where 0.5 is the baked tip offset in TC geometry.
         */
        getTransformHandlesScreenPos: () => {
            var _a, _b, _c;
            const tc = (_a = viewer.designProMain) === null || _a === void 0 ? void 0 : _a.transformControlsInitial;
            if (!(tc === null || tc === void 0 ? void 0 : tc.object) || tc.mode !== 'translate') {
                return null;
            }
            const translateGroup = (_c = (_b = tc._gizmo) === null || _b === void 0 ? void 0 : _b.gizmo) === null || _c === void 0 ? void 0 : _c.translate;
            if (!translateGroup) {
                return null;
            }
            // Gizmo origin = TC's own world position (where it was attached to the object)
            const gizmoCenter = tc.getWorldPosition(new three_1.Vector3());
            const result = {};
            // Arrow tip baked offsets (local gizmo space, before scale).
            // These match the arrow geometry in TransformControlsInitial.js.
            const bakedOffsets = {
                X: [0.5, 0, 0],
                Y: [0, 0.5, 0],
                Z: [0, 0, -0.5],
            };
            // All handles share the same scale factor.
            let gizmoScale = 1;
            translateGroup.children.forEach((child) => {
                var _a;
                if (child.isMesh && ((_a = child.scale) === null || _a === void 0 ? void 0 : _a.x) > 0.0001) {
                    gizmoScale = child.scale.x;
                }
            });
            for (const axis of ['X', 'Y', 'Z']) {
                const [bx, by, bz] = bakedOffsets[axis];
                result[axis] = worldToScreen(viewer, {
                    x: gizmoCenter.x + bx * gizmoScale,
                    y: gizmoCenter.y + by * gizmoScale,
                    z: gizmoCenter.z + bz * gizmoScale,
                });
            }
            if (!result['X'] && !result['Y'] && !result['Z']) {
                return null;
            }
            return result;
        },
        /**
         * Returns all current end-cap placement cone markers managed by CapHelper.
         * isApplied=false → red cone (no end cap placed at this tube end).
         * isApplied=true  → green cone (end cap already placed).
         * worldPos is the world-space position of the cone mesh.
         * screenPos is the screen-space position — use this to click the cone.
         */
        getEndCapCones: () => {
            var _a;
            const capHelper = viewer.designProMain.capHelper;
            const cones = (_a = capHelper === null || capHelper === void 0 ? void 0 : capHelper.cones) !== null && _a !== void 0 ? _a : [];
            return cones.map((c) => ({
                extrusionId: c.extrusionId,
                side: c.side,
                isApplied: c.capId !== null,
                worldPos: { x: c.mesh.position.x, y: c.mesh.position.y, z: c.mesh.position.z },
                screenPos: worldToScreen(viewer, c.mesh.position),
            }));
        },
        isGizmoActive() {
            var _a, _b, _c;
            const tc = (_a = viewer.designProMain) === null || _a === void 0 ? void 0 : _a.transformControlsInitial;
            if (!(tc === null || tc === void 0 ? void 0 : tc.object) || tc.mode !== 'translate') {
                return false;
            }
            const translateGroup = (_c = (_b = tc._gizmo) === null || _b === void 0 ? void 0 : _b.gizmo) === null || _c === void 0 ? void 0 : _c.translate;
            return !!(tc.object &&
                translateGroup);
        },
        getAllPlaneSnapInfo() {
            const results = [];
            viewer.scene.traverse((obj) => {
                if (obj.name === 'planeSnap1' || obj.name === 'planeSnap2') {
                    results.push({
                        x: +obj.position.x.toFixed(2),
                        y: +obj.position.y.toFixed(2),
                        z: +obj.position.z.toFixed(2),
                    });
                }
            });
            return results;
        },
    };
}
exports.setupViewerTestBridge = setupViewerTestBridge;
